import React, { FC, useMemo, useState } from 'react'
import { Typography, Flex, Spin, Empty, Radio, Divider } from 'antd'
import { BarChartOutlined, TableOutlined } from '@ant-design/icons'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { SectionCard, LegendDot } from 'containers/VerticalPage/common/antd'
import { formatNumber } from 'utils/formatters'
import useAggregateFlows, { AggregateFlowEntry } from './useAggregateFlows'

const { Text } = Typography

const CHART_COLORS = {
  deposits: '#FA8C16',
  withdrawals: '#2288B6',
}

const MAX_LABEL_LENGTH = 16

const truncateLabel = (label: string): string =>
  label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH)}…` : label

const formatActivityDate = (dateStr: string | null): string => {
  if (!dateStr) return '—'
  return dayjs(dateStr).format('D.MM.YYYY')
}

const CustomTooltip: FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const entry: AggregateFlowEntry = payload[0]?.payload
  if (!entry) return null

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        padding: '12px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontFamily: 'Roboto',
        minWidth: 200,
      }}
    >
      <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
        {entry.name}
      </Text>

      <Divider style={{ margin: '8px 0' }} />

      <Flex justify='space-between' gap={24} style={{ marginBottom: 4 }}>
        <div>
          <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
            Total Deposits
          </Text>
          <Text style={{ fontSize: 13, fontWeight: 500 }}>{formatNumber(entry.outflows)}</Text>
        </div>
        <div>
          <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
            Total withdrawals
          </Text>
          <Text style={{ fontSize: 13, fontWeight: 500 }}>{formatNumber(entry.inflows)}</Text>
        </div>
      </Flex>

      <Divider style={{ margin: '8px 0' }} />

      <div style={{ marginBottom: 4 }}>
        <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
          Total Activities
        </Text>
        <Text style={{ fontSize: 13, fontWeight: 500 }}>{entry.totalActivities.toLocaleString()} transactions</Text>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <Flex justify='space-between' gap={24}>
        <div>
          <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
            First Activity
          </Text>
          <Text style={{ fontSize: 13, fontWeight: 500 }}>{formatActivityDate(entry.firstActivity)}</Text>
        </div>
        <div>
          <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
            Last Activity
          </Text>
          <Text style={{ fontSize: 13, fontWeight: 500 }}>{formatActivityDate(entry.lastActivity)}</Text>
        </div>
      </Flex>
    </div>
  )
}

const TABLE_COLUMNS: ColumnsType<AggregateFlowEntry> = [
  {
    title: 'Exchange',
    dataIndex: 'name',
    key: 'name',
    render: (val: string) => (
      <Text style={{ fontFamily: 'Roboto', fontSize: 13 }} title={val}>
        {truncateLabel(val)}
      </Text>
    ),
  },
  {
    title: 'Total Deposits',
    dataIndex: 'outflows',
    key: 'outflows',
    align: 'right',
    sorter: (a, b) => a.outflows - b.outflows,
    render: (val: number) => (
      <Text style={{ color: CHART_COLORS.deposits, fontFamily: 'Roboto', fontWeight: 500 }}>{formatNumber(val)}</Text>
    ),
  },
  {
    title: 'Total Withdrawals',
    dataIndex: 'inflows',
    key: 'inflows',
    align: 'right',
    sorter: (a, b) => a.inflows - b.inflows,
    render: (val: number) => (
      <Text style={{ color: CHART_COLORS.withdrawals, fontFamily: 'Roboto', fontWeight: 500 }}>
        {formatNumber(val)}
      </Text>
    ),
  },
  {
    title: 'Activities',
    dataIndex: 'totalActivities',
    key: 'totalActivities',
    align: 'right',
    sorter: (a, b) => a.totalActivities - b.totalActivities,
    render: (val: number) => <Text style={{ fontFamily: 'Roboto', fontSize: 13 }}>{val.toLocaleString()}</Text>,
  },
  {
    title: 'First Activity',
    dataIndex: 'firstActivity',
    key: 'firstActivity',
    render: (val: string | null) => (
      <Text style={{ fontFamily: 'Roboto', fontSize: 13 }}>{formatActivityDate(val)}</Text>
    ),
  },
  {
    title: 'Last Activity',
    dataIndex: 'lastActivity',
    key: 'lastActivity',
    render: (val: string | null) => (
      <Text style={{ fontFamily: 'Roboto', fontSize: 13 }}>{formatActivityDate(val)}</Text>
    ),
  },
]

const AggregateFlowsChart: FC = () => {
  const { data, isLoading } = useAggregateFlows()
  const [view, setView] = useState<string>('chart')

  const totals = useMemo(
    () => ({
      deposits: data.reduce((sum, d) => sum + d.outflows, 0),
      withdrawals: data.reduce((sum, d) => sum + d.inflows, 0),
    }),
    [data],
  )

  if (isLoading) {
    return (
      <SectionCard>
        <Flex justify='center' align='center' style={{ height: 300 }}>
          <Spin />
        </Flex>
      </SectionCard>
    )
  }

  if (!data.length) {
    return (
      <SectionCard title='Aggregate Deposits (Outflows) &amp; Withdrawals (Inflows) per Exchange'>
        <Empty description='No interactions with Centralized Exchanges' />
      </SectionCard>
    )
  }

  return (
    <SectionCard
      title={
        <Flex justify='space-between' align='center'>
          <span>Aggregate Deposits (Outflows) &amp; Withdrawals (Inflows) per Exchange</span>
          <Radio.Group value={view} onChange={e => setView(e.target.value)} size='small' buttonStyle='solid'>
            <Radio.Button value='chart'>
              <BarChartOutlined /> Chart
            </Radio.Button>
            <Radio.Button value='table'>
              <TableOutlined /> Table
            </Radio.Button>
          </Radio.Group>
        </Flex>
      }
    >
      <Flex gap={16} style={{ marginBottom: 12 }}>
        <LegendDot color={CHART_COLORS.deposits} label={`Deposits: ${formatNumber(totals.deposits)}`} />
        <LegendDot color={CHART_COLORS.withdrawals} label={`Withdrawals: ${formatNumber(totals.withdrawals)}`} />
      </Flex>

      {view === 'chart' ? (
        <div style={{ width: '100%', height: 205 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={data} barGap={4} barCategoryGap={8}>
              <CartesianGrid vertical={false} stroke='#f0f0f0' />
              <XAxis
                dataKey='name'
                tickLine={false}
                axisLine={{ stroke: '#f0f0f0' }}
                tick={{ fontSize: 14, fontFamily: 'Source Sans Pro', fontWeight: 500, fill: '#000' }}
                tickFormatter={truncateLabel}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 14, fontFamily: 'Source Sans Pro', fill: '#546E7A' }}
                tickFormatter={(val: number) => formatNumber(val, undefined, { notation: 'compact', style: 'decimal' }) ?? ''}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend wrapperStyle={{ display: 'none' }} />
              <Bar dataKey='outflows' fill={CHART_COLORS.deposits} radius={[5, 5, 0, 0]} barSize={10} name='Deposits' />
              <Bar
                dataKey='inflows'
                fill={CHART_COLORS.withdrawals}
                radius={[5, 5, 0, 0]}
                barSize={10}
                name='Withdrawals'
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Table
          columns={TABLE_COLUMNS}
          dataSource={data}
          rowKey='name'
          size='small'
          pagination={false}
          scroll={{ y: 260 }}
        />
      )}
    </SectionCard>
  )
}

export default AggregateFlowsChart
